import "./assets/scss/style.scss";

import { lazy, useEffect } from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";

import { DivisionAwareThemeProvider } from "./core/division-aware-theme-provider";
import { useEmbeddedHelper } from "./core/navigation/use-embedded-helper";
import { ViewerRouteBodyAttribute } from "./core/viewer-route";
import { WEB_CONFIG } from "./core/web-config";
import { LandingPage } from "./landing-page";
import { SuspenseShimmerWithUI } from "./suspense";

const AppBootstrap = lazy(
  () => import(/* webpackChunkName: "bootstrap" */ "./app-bootstrap")
);
const Dashboard = lazy(
  () => import(/* webpackChunkName: "dashboard" */ "./dashboard/dashboard")
);
// Named so the build plugin can keep it OUT of the index.html preload list
// (hotlink is a separate route — only loads on /_hotlink).
const Hotlink = lazy(
  () => import(/* webpackChunkName: "hotlink" */ "./hotlink/hotlink")
);

export function App() {
  const embedded = useEmbeddedHelper();

  useEffect(() => {
    if (embedded.isEmbedded) {
      document.body.setAttribute("data-embed", "true");
    } else {
      document.body.removeAttribute("data-embed");
    }
  }, [embedded.isEmbedded]);

  return (
    <DivisionAwareThemeProvider>
      <Router>
        <ViewerRouteBodyAttribute />
        <Switch>
          <Route path={"/_hotlink"}>
            <SuspenseShimmerWithUI>
              <AppBootstrap>
                <Hotlink />
              </AppBootstrap>
            </SuspenseShimmerWithUI>
          </Route>
          {WEB_CONFIG.IS_CATCH_ALL_DOMAIN ? (
            <Route path={"/:id(div_[a-zA-Z0-9]{8})/_hotlink"}>
              <SuspenseShimmerWithUI>
                <AppBootstrap>
                  <Hotlink />
                </AppBootstrap>
              </SuspenseShimmerWithUI>
            </Route>
          ) : null}

          {/* Organization-owned story preview — same render as a division
              hotlink; the company is injected server-side as DIVISION_INFO.
              Two plain patterns (react-router's path-to-regexp rejects the
              (?:…) group Express accepts). */}
          {WEB_CONFIG.IS_CATCH_ALL_DOMAIN ? (
            <Route
              path={[
                "/:id(com_[a-zA-Z0-9]{7})/_hotlink",
                "/:id(cntry_[a-zA-Z0-9]{7})/_hotlink",
              ]}
            >
              <SuspenseShimmerWithUI>
                <AppBootstrap>
                  <Hotlink />
                </AppBootstrap>
              </SuspenseShimmerWithUI>
            </Route>
          ) : null}

          {WEB_CONFIG.IS_CATCH_ALL_DOMAIN ? (
            <Route path="/:id(div_[a-zA-Z0-9]{8})">
              <SuspenseShimmerWithUI>
                <AppBootstrap>
                  <Dashboard />
                </AppBootstrap>
              </SuspenseShimmerWithUI>
            </Route>
          ) : null}

          {WEB_CONFIG.IS_CATCH_ALL_DOMAIN ? (
            <Route path="/:id([a-zA-Z0-9]{7})">
              <SuspenseShimmerWithUI>
                <AppBootstrap>
                  <Dashboard />
                </AppBootstrap>
              </SuspenseShimmerWithUI>
            </Route>
          ) : null}

          {WEB_CONFIG.IS_CATCH_ALL_DOMAIN ? (
            <Route>
              <LandingPage />
            </Route>
          ) : (
            <Route>
              <SuspenseShimmerWithUI>
                <AppBootstrap>
                  <Dashboard />
                </AppBootstrap>
              </SuspenseShimmerWithUI>
            </Route>
          )}
        </Switch>
      </Router>
    </DivisionAwareThemeProvider>
  );
}
